import { useState, useCallback, useRef } from "react";
import { Plus, UploadSimple, DownloadSimple, Barcode, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import BarcodeScanner from "./BarcodeScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseForm({ onSubmit, categories = [], onBulkUploadComplete }) {
  const [form, setForm] = useState({
    fecha: new Date(),
    articulo: "",
    cantidad: "",
    precio_articulo: "",
    categoria: "Otros",
    codigo_barras: ""
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.articulo || !form.cantidad || !form.precio_articulo) {
      toast.error("Por favor, completa todos los campos obligatorios");
      return;
    }

    const success = await onSubmit({
      fecha: format(form.fecha, "yyyy-MM-dd"),
      articulo: form.articulo.trim().toUpperCase(),
      cantidad: parseFloat(form.cantidad),
      precio_articulo: parseFloat(form.precio_articulo),
      categoria: form.categoria,
      codigo_barras: form.codigo_barras.trim() || null
    });

    if (success) {
      setForm({ fecha: new Date(), articulo: "", cantidad: "", precio_articulo: "", categoria: "Otros", codigo_barras: "" });
    }
  }, [form, onSubmit]);

  const handleDateSelect = useCallback((date) => {
    setForm(prev => ({ ...prev, fecha: date || new Date() }));
    setDateOpen(false);
  }, []);

  const handleInputChange = useCallback((field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  const handleCategoryChange = useCallback((value) => {
    setForm(prev => ({ ...prev, categoria: value }));
  }, []);

  // Barcode lookup
  const lookupBarcode = useCallback(async (barcode) => {
    if (!barcode.trim()) return;
    
    try {
      const response = await axios.get(`${API}/articles/by-barcode/${barcode.trim()}`);
      const article = response.data;
      setForm(prev => ({
        ...prev,
        codigo_barras: barcode,
        articulo: article.nombre,
        precio_articulo: article.precio.toString(),
        categoria: article.categoria || "Otros",
        cantidad: prev.cantidad || "1"
      }));
      toast.success(`Artículo encontrado: ${article.nombre}`);
    } catch (error) {
      if (error.response?.status === 404) {
        // Article not found - set barcode and default quantity
        setForm(prev => ({
          ...prev,
          codigo_barras: barcode,
          cantidad: prev.cantidad || "1"
        }));
        toast.info("Artículo nuevo. Completa los datos.");
      } else {
        console.error("Error looking up barcode:", error);
      }
    }
  }, []);

  const handleBarcodeBlur = useCallback(() => {
    lookupBarcode(form.codigo_barras);
  }, [form.codigo_barras, lookupBarcode]);

  // Handle scanned barcode
  const handleBarcodeScan = useCallback((barcode) => {
    lookupBarcode(barcode);
  }, [lookupBarcode]);

  const handleDownloadTemplate = useCallback(() => {
    window.open(`${API}/purchases/template`, '_blank');
  }, []);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/purchases/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(response.data);
      
      if (response.data.total_procesados > 0) {
        toast.success(`${response.data.total_procesados} compras procesadas correctamente`);
        if (onBulkUploadComplete) {
          onBulkUploadComplete();
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Error al procesar el archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [onBulkUploadComplete]);

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 justify-end">
        {/* Scan Barcode Button */}
        <Button 
          data-testid="scan-barcode-btn"
          variant="outline"
          className="gap-2"
          onClick={() => setShowScanner(true)}
        >
          <Camera size={16} />
          Escanear Código
        </Button>

        {/* Bulk Upload Button */}
        <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
          <DialogTrigger asChild>
            <Button 
              data-testid="bulk-upload-btn"
              variant="outline"
              className="gap-2"
            >
              <UploadSimple size={16} />
              Subida Masiva
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Subida Masiva de Compras</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Sube un archivo Excel con tus compras. Si el código de barras ya existe, 
                solo necesitas el código y la cantidad (por defecto 1).
              </p>
              
              <Button
                data-testid="download-template-btn"
                variant="outline"
                onClick={handleDownloadTemplate}
                className="w-full gap-2"
              >
                <DownloadSimple size={16} />
                Descargar Plantilla
              </Button>

              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="bulk-upload-input"
                  data-testid="bulk-upload-input"
                />
                <label 
                  htmlFor="bulk-upload-input" 
                  className="cursor-pointer"
                >
                  <UploadSimple size={32} className="mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">
                    {uploading ? "Procesando..." : "Haz clic para seleccionar archivo Excel"}
                  </p>
                </label>
              </div>

              {uploadResult && (
                <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
                  <p><strong>Procesados:</strong> {uploadResult.total_procesados}</p>
                  <p><strong>Artículos nuevos:</strong> {uploadResult.articulos_nuevos}</p>
                  <p><strong>Artículos actualizados:</strong> {uploadResult.articulos_actualizados}</p>
                  {uploadResult.errores.length > 0 && (
                    <div className="mt-2">
                      <p className="text-red-600 font-medium">Errores:</p>
                      <ul className="list-disc list-inside text-red-600">
                        {uploadResult.errores.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Manual Purchase Form */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="space-y-2">
          <Label>Código Barras</Label>
          <div className="relative">
            <Barcode size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="purchase-barcode-input"
              placeholder="Escanear o escribir"
              value={form.codigo_barras}
              onChange={handleInputChange("codigo_barras")}
              onBlur={handleBarcodeBlur}
              className="pl-9 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowScanner(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
              title="Escanear con cámara"
            >
              <Camera size={18} />
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Fecha</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="purchase-date-btn"
                className="w-full justify-start text-left font-normal"
              >
                {format(form.fecha, "dd/MM/yyyy", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={form.fecha}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label>Artículo *</Label>
          <Input
            data-testid="purchase-articulo-input"
            placeholder="Nombre del artículo"
            value={form.articulo}
            onChange={handleInputChange("articulo")}
          />
        </div>
        <div className="space-y-2">
          <Label>Cantidad *</Label>
          <Input
            data-testid="purchase-cantidad-input"
            type="number"
            step="0.01"
            placeholder="0"
            value={form.cantidad}
            onChange={handleInputChange("cantidad")}
          />
        </div>
        <div className="space-y-2">
          <Label>Precio (€) *</Label>
          <Input
            data-testid="purchase-precio-input"
            type="number"
            step="0.01"
            placeholder="0.00"
            value={form.precio_articulo}
            onChange={handleInputChange("precio_articulo")}
          />
        </div>
        <div className="space-y-2">
          <Label>Categoría</Label>
          <Select value={form.categoria} onValueChange={handleCategoryChange}>
            <SelectTrigger data-testid="purchase-category-select">
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-full">
          <Button 
            data-testid="add-purchase-btn"
            type="submit" 
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus size={16} className="mr-2" />
            Añadir Compra
          </Button>
        </div>
      </form>

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        open={showScanner}
        onOpenChange={setShowScanner}
        onScan={handleBarcodeScan}
      />
    </div>
  );
}
